import { useState } from "react";
import { MailCheck, Mail, CheckCircle2, AlertCircle, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useVerifyResend from "@/hooks/useVerifyResend";

/**
 * App-wide banner shown to authenticated users whose email is not yet verified.
 * - Hidden for verified users, unauthenticated users, and when dismissed for the session.
 * - Uses the existing POST /api/auth/verify/request endpoint via useVerifyResend.
 */
export default function EmailVerifyBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const { state, message, resend, cooldownLeft, isBusy } = useVerifyResend();

  if (!user || user.email_verified || dismissed) return null;

  // Success surfaces a solid success card; error surfaces a red bar; otherwise
  // the neutral warm-orange verify banner (NContro brand).
  const success = state === "sent";
  const error = state === "error";

  return (
    <div
      data-testid="verify-banner"
      role="status"
      aria-live="polite"
      className={
        "border-b " +
        (success
          ? "bg-emerald-50 border-emerald-200"
          : error
          ? "bg-red-50 border-red-200"
          : "bg-[#FFF6EC] border-[#F97316]/20")
      }
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <span className={"shrink-0 " + (success ? "text-emerald-600" : error ? "text-red-600" : "text-[#C2410C]")}>
          {success ? <CheckCircle2 className="w-5 h-5" /> : error ? <AlertCircle className="w-5 h-5" /> : <MailCheck className="w-5 h-5" />}
        </span>

        <div className="flex-1 min-w-0 text-[13.5px] leading-snug">
          {success ? (
            <span className="text-emerald-800">
              <strong className="font-semibold">{message}</strong>{" "}
              <button
                onClick={() => refresh()}
                data-testid="verify-banner-refresh"
                className="underline underline-offset-2 hover:text-emerald-900"
              >
                I've clicked the link — refresh
              </button>
            </span>
          ) : error ? (
            <span className="text-red-800">
              <strong className="font-semibold">{message}</strong>
            </span>
          ) : (
            <span className="text-[#7C2D0E]">
              <strong className="font-semibold text-neutral-900">Your email has not been verified yet.</strong>{" "}
              <span className="text-neutral-700">
                {user.role === "vendor"
                  ? "Verify your email to receive quote requests and enable payments."
                  : user.role === "admin"
                  ? "Verify your email to secure your account."
                  : "Verify your email to unlock bookings and payments."}
              </span>
            </span>
          )}
        </div>

        <button
          onClick={resend}
          disabled={isBusy}
          data-testid="verify-banner-resend"
          className={
            "shrink-0 inline-flex items-center gap-1.5 rounded-full text-white text-[13px] font-semibold px-4 py-1.5 transition-colors " +
            (isBusy
              ? "bg-neutral-400 cursor-not-allowed"
              : "bg-[#F97316] hover:bg-[#EA6A11] active:bg-[#D55F0E]")
          }
        >
          <Mail className="w-3.5 h-3.5" strokeWidth={2.4} />
          {state === "sending"
            ? "Sending…"
            : cooldownLeft > 0
            ? `Resend in ${cooldownLeft}s`
            : success
            ? "Resend"
            : "Resend verification email"}
        </button>

        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss verification reminder"
          data-testid="verify-banner-dismiss"
          className="shrink-0 -mr-1 p-1 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-white/60"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
